import type { EbvAverageData } from '@/hooks/publicSearch/ebv/ebvColumns';
import { parseBreedAverageLines } from '@/components/publicSearch/animal/ebv/breedAverageDisplay';

export type BreedAverageBand = 'B' | 'R';

export type BreedAverageCaptionLine = {
  caption: string;
  band?: BreedAverageBand;
};

/** True when setting 15 carries CANG B/R breed-average cohort data (both bands). */
function hasBlackRedBreedAverageData(
  ebvAverage: EbvAverageData | null | undefined,
): boolean {
  if (!ebvAverage) return false;
  const hasBothCaptions =
    Boolean(ebvAverage.avgCaptionB?.trim()) &&
    Boolean(ebvAverage.avgCaptionR?.trim());
  const hasBothAvgValues =
    Boolean(ebvAverage.ebv_1_avg_b?.trim()) &&
    Boolean(ebvAverage.ebv_1_avg_r?.trim());
  return hasBothCaptions || hasBothAvgValues;
}

/** Grid / createAverageRow: CANG strain flag or setting-15 B/R cohort fields present. */
export function shouldStackBlackRedBreedAverages(
  isBlackRedAngus: boolean,
  ebvAverage: EbvAverageData | null | undefined,
): boolean {
  return isBlackRedAngus || hasBlackRedBreedAverageData(ebvAverage);
}

function expandCangCaptions(
  blackRaw?: string,
  redRaw?: string,
): { black?: string; red?: string } {
  let black = blackRaw?.trim();
  let red = redRaw?.trim();

  if (black && !red) {
    const parts = parseBreedAverageLines(black);
    if (parts.length >= 2) {
      return { black: parts[0], red: parts[1] };
    }
  }
  if (!black && red) {
    const parts = parseBreedAverageLines(red);
    if (parts.length >= 2) {
      return { black: parts[0], red: parts[1] };
    }
  }

  return { black, red };
}

/**
 * Strip ILR2 emphasis / placeholder markers (`***`) from run captions.
 * Dairy/i4 examples: "*** *** April 26" → "April 26"; empty-after-strip hides the header.
 */
function sanitizeEbvRunLabel(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw
    .replace(/\*{2,}/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Header run label from animal `ebvDescription`, else setting 15 `runLabel` (i4 parity). */
export function resolveRunLabel(
  ebvDescription: string | null | undefined,
  runLabel: string | null | undefined,
): string {
  const animal = sanitizeEbvRunLabel(ebvDescription);
  if (animal) return animal;
  return sanitizeEbvRunLabel(runLabel);
}

/** Animal-detail / grid breed-average captions (single avgsCaption or CANG B/R lines). */
export function resolveDetailAvgCaptions(
  useBlackRedCaptions: boolean,
  ebvAverage: EbvAverageData | null | undefined,
  showPercentileSearch: boolean,
): BreedAverageCaptionLine[] {
  if (!ebvAverage) return [];

  if (useBlackRedCaptions) {
    const { black, red } = expandCangCaptions(
      ebvAverage.avgCaptionB,
      ebvAverage.avgCaptionR,
    );
    const lines: BreedAverageCaptionLine[] = [];
    if (black) {
      lines.push({
        caption: black,
        band: showPercentileSearch ? 'B' : undefined,
      });
    }
    if (red) {
      lines.push({
        caption: red,
        band: showPercentileSearch ? 'R' : undefined,
      });
    }
    return lines;
  }

  const avgs = ebvAverage.avgsCaption?.trim();
  return avgs ? [{ caption: avgs }] : [];
}

/** Grid breed-average row label (ANIMAL_NAME column); CANG defers display to {@link BreedAverageGridRowLabel}. */
export function resolveGridAvgRowCaptions(
  useBlackRedCaptions: boolean,
  ebvAverage: EbvAverageData | null | undefined,
  _showPercentileSearch?: boolean,
): string | undefined {
  if (!ebvAverage) return undefined;

  if (useBlackRedCaptions) {
    const { black, red } = expandCangCaptions(
      ebvAverage.avgCaptionB,
      ebvAverage.avgCaptionR,
    );
    return black || red || undefined;
  }

  return ebvAverage.avgsCaption?.trim() || undefined;
}

/** Percentile / potential-change graph footer from setting 15. */
export function resolveGraphFooterCaption(
  ebvAverage: EbvAverageData | null | undefined,
): string | undefined {
  return ebvAverage?.graphFooterCaption?.trim() || undefined;
}

/** EBV percentiles page header caption; band-aware for CANG B/R views. */
export function resolvePercentilesPageCaption(
  ebvAverage: EbvAverageData | null | undefined,
  band: BreedAverageBand | null,
): string | undefined {
  if (!ebvAverage) return undefined;

  if (band === 'B') {
    const black = ebvAverage.avgCaptionB?.trim();
    if (black) return black;
  }
  if (band === 'R') {
    const red = ebvAverage.avgCaptionR?.trim();
    if (red) return red;
  }

  return (
    ebvAverage.medianCaption?.trim() ||
    ebvAverage.avgsCaption?.trim() ||
    undefined
  );
}
